import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
// Import Types
import type { RouteListType } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectPartnerId } from 'store/slices/partnerSettings';
// Import Hooks
import useGoToRoute from './GoToRoute';
import useQueryParams from './UseQueryParams';
// Import Helpers
import { getFilterHolderKey } from 'services/Helpers';
// Import Configs - using direct imports to avoid circular dependencies
import { FilterHolderFieldsConfigs as SegmentConfigs } from 'pages/SegmentListPage/configs';
import { FilterHolderFieldsConfigs as TemplateConfigs } from 'pages/TemplateListPage/configs';
import { FilterHolderFieldsConfigs as CustomerJourneyConfigs } from 'pages/CustomerJourneyListPage/configs';
import { FilterHolderFieldsConfigs as ReportConfigs } from 'pages/ReportListPage/configs';
import { globalSearchObjectKeys, searchActionKeys } from 'components/GlobalSearch/config';
// Import Constants
import { RoutesList } from 'routes';
import {
    CustomerJourneyStatusLabels,
    l,
    RealtimePromotionTypes,
    SegmentTypeIcons,
    TemplateTypeIcons,
    ReportTypeIcons,
    RealtimePromotionTypeIcons,
    ReportTypesLabels,
} from 'constants/common';
// Import Enums
import { ObjectTypeIdsEnum } from 'enums';

const ObjectTypeIcons = {
    [ObjectTypeIdsEnum.Segment]: ({ Type }) => SegmentTypeIcons[Type],
    [ObjectTypeIdsEnum.Template]: ({ DeliveryMethod }) => TemplateTypeIcons[DeliveryMethod],
    [ObjectTypeIdsEnum.Campaign]: () => 'bc-icon-campaign',
    [ObjectTypeIdsEnum.RealtimePromotion]: ({ PromotionType }) => RealtimePromotionTypeIcons[PromotionType],
    [ObjectTypeIdsEnum.Report]: ({ ReportType }) => ReportTypeIcons[ReportType],
};

const buildRouteWithResetTable = (route: RouteListType[keyof RouteListType]) => {
    return { ...route, shouldResetTable: true };
};

const getRowAction = (data) => {
    const { row, goToRoute, encode, partnerId, setOpenedModalData, modalsStateSetDict, closePopover, t } = data;

    switch (row.ObjectTypeId) {
        case ObjectTypeIdsEnum.Segment:
            return [
                {
                    icon: 'bc-icon-new-window',
                    actionKey: searchActionKeys.view,
                    name: t(l.ViewSegment),
                    description: t(l.ViewSegment),
                    onClick: () => {
                        closePopover && closePopover();

                        const params = encode({
                            [getFilterHolderKey(l.SegmentList)]: {
                                [SegmentConfigs.idFieldName]: row.ObjectId.toString(),
                            },
                            partnerId,
                        });

                        goToRoute(buildRouteWithResetTable(RoutesList.SEGMENT_LIST), null, params);
                    },
                },
                {
                    icon: 'bc-icon-info',
                    actionKey: searchActionKeys.info,
                    name: t(l.SegmentInfo),
                    description: t(l.SegmentInfo),
                    onClick: () => {
                        closePopover && closePopover();
                        setOpenedModalData({
                            [globalSearchObjectKeys.segment]: {
                                id: row.ObjectId.toString(),
                                type: row.ListItem?.Type,
                                definitionType: row.ListItem?.DefinitionType,
                                targetObject: row.ListItem?.TargetObject,
                            },
                        });

                        modalsStateSetDict.infoSegment.fn(true);
                    },
                },
            ];
        case ObjectTypeIdsEnum.Template:
            return [
                {
                    icon: 'bc-icon-new-window',
                    actionKey: searchActionKeys.view,
                    name: t(l.ViewTemplate),
                    description: t(l.ViewTemplate),
                    onClick: () => {
                        closePopover && closePopover();

                        const params = encode({
                            [getFilterHolderKey(l.TemplateList)]: {
                                [TemplateConfigs.idFieldName]: row.ObjectId.toString(),
                            },
                            partnerId,
                        });

                        goToRoute(buildRouteWithResetTable(RoutesList.TEMPLATE_LIST), null, params);
                    },
                },
                {
                    icon: 'bc-icon-info',
                    actionKey: searchActionKeys.info,
                    name: t(l.TemplateInfo),
                    description: t(l.TemplateInfo),
                    onClick: () => {
                        closePopover && closePopover();
                        setOpenedModalData({
                            [globalSearchObjectKeys.template]: {
                                id: row.ObjectId.toString(),
                                childItemsInfo: row.ListItem?.ChildItemsInfo,
                                isGlobal: row.ListItem?.IsGlobal,
                            },
                        });
                        modalsStateSetDict.infoTemplate.fn(true);
                    },
                },
            ];
        case ObjectTypeIdsEnum.Campaign:
            return [
                {
                    icon: 'bc-icon-new-window',
                    actionKey: searchActionKeys.view,
                    name: t(l.ViewCampaign),
                    description: t(l.ViewCampaign),
                    onClick: () => {
                        closePopover && closePopover();
                        goToRoute(RoutesList.CAMPAIGN_REPORT, { campaignId: row.ObjectId.toString() });
                    },
                },
                {
                    icon: 'bc-icon-info',
                    actionKey: searchActionKeys.info,
                    name: t(l.CampaignInfo),
                    description: t(l.CampaignInfo),
                    onClick: () => {
                        closePopover && closePopover();
                        setOpenedModalData({
                            [globalSearchObjectKeys.campaign]: {
                                id: row.ObjectId.toString(),
                            },
                        });

                        modalsStateSetDict.infoCampaign.fn(true);
                    },
                },
            ];
        case ObjectTypeIdsEnum.RealtimePromotion: {
            const isViewPageAvailable = ![
                +CustomerJourneyStatusLabels.Updating,
                +CustomerJourneyStatusLabels.Starting,
                +CustomerJourneyStatusLabels.Resuming,
                +CustomerJourneyStatusLabels.Suspending,
                +CustomerJourneyStatusLabels.Invalid,
            ].includes(row.ListItem?.Status);

            return [
                {
                    icon: 'bc-icon-new-window',
                    actionKey: searchActionKeys.view,
                    name: t(l.ViewRealtimePromotion),
                    description: t(l.ViewRealtimePromotion),
                    onClick: () => {
                        closePopover && closePopover();
                        if (isViewPageAvailable) {
                            const route =
                                row.ListItem?.PromotionType === +RealtimePromotionTypes.ActionCampaign
                                    ? RoutesList.REALTIME_ACTION_VIEW
                                    : RoutesList.REALTIME_JOURNEY_VIEW;

                            goToRoute(route, {
                                workflowId: row.ObjectId.toString(),
                            });
                        } else {
                            const params = encode({
                                [getFilterHolderKey(l.JourneyPage)]: {
                                    [CustomerJourneyConfigs.idFieldName]: row.ObjectId.toString(),
                                },
                                partnerId,
                            });

                            goToRoute(buildRouteWithResetTable(RoutesList.REALTIME_PROMOTIONS_LIST), null, params);
                        }
                    },
                },
                {
                    icon: 'bc-icon-info',
                    actionKey: searchActionKeys.info,
                    name: t(l.CJInfo),
                    description: t(l.CJInfo),
                    onClick: () => {
                        closePopover && closePopover();
                        setOpenedModalData({
                            [globalSearchObjectKeys.realtimePromotion]: {
                                WorkflowId: row.ObjectId.toString(),
                                PromotionType: row.ListItem?.PromotionType,
                                HasVisualization: row.ListItem?.HasVisualization,
                            },
                        });

                        modalsStateSetDict.infoCustomerJourney.fn(true);
                    },
                },
            ];
        }
        case ObjectTypeIdsEnum.Report: {
            const isViewPageAvailable = row.ListItem?.SelfExecutionCount > 0;
            const searchActions = [
                {
                    icon: 'bc-icon-new-window',
                    actionKey: searchActionKeys.view,
                    name: t(l.ViewReport),
                    description: t(l.ViewReport),
                    onClick: () => {
                        closePopover && closePopover();
                        if (isViewPageAvailable) {
                            goToRoute(RoutesList.REPORT_EXECUTED_LIST, {
                                reportId: row.ObjectId.toString(),
                                reportType: row.ListItem?.ReportType,
                            });
                        } else {
                            const params = encode({
                                [getFilterHolderKey(l.ReportList)]: {
                                    [ReportConfigs.idFieldName]: row.ObjectId.toString(),
                                },
                                partnerId,
                            });

                            goToRoute(buildRouteWithResetTable(RoutesList.REPORT_LIST), null, params);
                        }
                    },
                },
            ];

            if (row.ListItem?.ReportType !== ReportTypesLabels.CustomReport) {
                searchActions.push({
                    icon: 'bc-icon-info',
                    actionKey: searchActionKeys.info,
                    name: t(l.ReportInfo),
                    description: t(l.ReportInfo),
                    onClick: () => {
                        closePopover && closePopover();
                        setOpenedModalData({
                            [globalSearchObjectKeys.report]: {
                                ReportId: row.ObjectId.toString(),
                                ReportType: row.ListItem?.ReportType,
                                ReportState: row.ListItem?.State,
                                SelfExecutionCount: row.ListItem?.SelfExecutionCount,
                                CompositeExecutionCount: row.ListItem?.CompositeExecutionCount,
                            },
                        });

                        modalsStateSetDict.infoReport.fn(true);
                    },
                });
            }

            return searchActions;
        }
        default:
            break;
    }
};

const mapToUIModel = (config) => {
    const { data, goToRoute, encode, partnerId, setOpenedModalData, modalsStateSetDict, closePopover, t, dateFormats } =
        config;
    const { partnerDateFormat } = dateFormats || {};
    return (data || []).map((row) => ({
        id: String(row.ObjectId),
        rowData: row,
        icon: ObjectTypeIcons[row.ObjectTypeId](row.ListItem),
        title: row.Name,
        name: row.ModifiedByName,
        actions: getRowAction({
            row,
            goToRoute,
            encode,
            partnerId,
            setOpenedModalData,
            modalsStateSetDict,
            closePopover,
            t,
        }),
        date: {
            date: moment(row.ModifiedDate).format(partnerDateFormat),
            labelText: t(l.ModifiedDate),
        },
    }));
};

const useSearchRedirectionAndModal = () => {
    const { t } = useTranslation();
    const partnerId = useAppSelector(selectPartnerId);
    const { goToRoute } = useGoToRoute();
    const { encode } = useQueryParams();

    const [openedModalData, setOpenedModalData] = useState({});
    const [isSegmentInfoModalOpen, setIsSegmentInfoModalOpen] = useState(false);
    const [isTemplateInfoModalOpen, setIsTemplateInfoModalOpen] = useState(false);
    const [isCampaignInfoModalOpen, setIsCampaignInfoModalOpen] = useState(false);
    const [isRealtimePromotionInfoModalOpen, setIsRealtimePromotionInfoModalOpen] = useState(false);
    const [isReportInfoModalOpen, setIsReportInfoModalOpen] = useState(false);

    const modalsStateSetDict = useMemo(
        () => ({
            infoSegment: {
                key: 'infoSegment',
                fn: setIsSegmentInfoModalOpen,
            },
            infoTemplate: {
                key: 'infoTemplate',
                fn: setIsTemplateInfoModalOpen,
            },
            infoCampaign: {
                key: 'infoCampaign',
                fn: setIsCampaignInfoModalOpen,
            },
            infoCustomerJourney: {
                key: 'infoCustomerJourney',
                fn: setIsRealtimePromotionInfoModalOpen,
            },
            infoReport: {
                key: 'infoReport',
                fn: setIsReportInfoModalOpen,
            },
        }),
        [],
    );

    const addActionsToData = useCallback(
        ({ data, closePopover, dateFormats }) => {
            return mapToUIModel({
                data,
                goToRoute,
                encode,
                partnerId,
                setOpenedModalData,
                modalsStateSetDict,
                closePopover,
                t,
                dateFormats,
            });
        },
        [encode, goToRoute, modalsStateSetDict, partnerId, t],
    );

    return {
        modalsStateSetDict,
        openedModalData,
        isSegmentInfoModalOpen,
        isTemplateInfoModalOpen,
        isCampaignInfoModalOpen,
        isRealtimePromotionInfoModalOpen,
        isReportInfoModalOpen,
        addActionsToData,
    };
};

export default useSearchRedirectionAndModal;
